<style lang="scss">
    .links line {
        stroke: #999;
        stroke-opacity: 0.6;
    }

    .nodes circle {
        stroke: #666;
        stroke-width: 1px;

        &[fill="url(#node-img-blood)"] {
            stroke: #fff;
            stroke-width: 2px;
        }
    }
    .tissue-web {
        display: flex;
        &__svg {
            flex: 1 1 auto;
        }
    }
</style>
<template>
    <div class="tissue-web">
        <tooltip v-bind="tooltip" />
        <svg class="tissue-web__svg" :viewBox="`0 0 ${width} ${height}`"></svg>
    </div>
</template>
<script>
import {
    select,
    forceCollide,
    forceSimulation,
    forceLink,
    forceManyBody,
    forceCenter,
    event,
} from 'd3'
const Tooltip = () => import(/* webpackChunkName: 'Tooltip' */ '@/components/Tooltip')

export default {
    name: 'TissueWeb',
    data: () => ({
        node: null,
        link: null,
        tooltip: {
            active: false,
            text: '',
            top: 0,
            left: 0,
        },
    }),
    props: {
        tissues: {
            type: Array,
            default: () => [],
        },
        width: {
            type: Number,
            default: 960,
        },
        height: {
            type: Number,
            default: 600,
        },
        radius: {
            type: Number,
            default: 35,
        },
    },
    computed: {
        links() {
            return this.tissues.reduce((sum, tissue) => [
                ...sum,
                ...(tissue.links || []).map(link => ({source: tissue.key, target: link})),
            ], [])
        },
        svg() {
            return select(this.$el.querySelector('.tissue-web__svg'))
        },
        collide() {
            return forceCollide().radius(d => {
                let obj = select('#' + d.key)
                let collisionRadius
                if (obj.attr('toradius')) {
                    collisionRadius = Number(obj.attr('toradius'))
                } else {
                    collisionRadius = Number(obj.attr('r'))
                }
                return collisionRadius + 10
            }).iterations(10)
        },
        simulation() {
            return forceSimulation()
                .force('link', forceLink().id(d => d.key).distance(200).strength(0.05))
                .force('charge', forceManyBody())
                .force('collide', this.collide)
                .force('center', forceCenter(this.width / 2, this.height / 2))
        },
    },
    methods: {
        ticked() {
            this.node
                .attr('cx', d => Math.max(this.radius, Math.min(this.width - this.radius, d.x)))
                .attr('cy', d => Math.max(this.radius, Math.min(this.height - this.radius, d.y)))
            this.link
                .attr('x1', d => d.source.x)
                .attr('y1', d => d.source.y)
                .attr('x2', d => d.target.x)
                .attr('y2', d => d.target.y)
        },
        mouseover() {
            let current = select(event.currentTarget)
            current.attr('toradius', 100).transition()
                .duration(250)
                .attr('r', 100)
                .style('background-size', 'cover')
                .style('backbround-position', 'center')

            current.select('rect').attr('x', '50%').attr('y', '50%')

            let offset = this.$el.querySelector('.tissue-web__svg').getBoundingClientRect()

            this.tooltip = {
                active: true,
                text: current.datum().name,
                top: event.pageY - offset.top - window.pageYOffset,
                left: event.pageX - offset.left - window.pageXOffset,
            }
            this.collide.initialize(this.simulation.nodes())
            this.simulation.alpha(1).restart()
        },
        mouseout() {
            select(event.currentTarget).attr('toradius', this.radius).transition()
                .duration(250)
                .attr('r', this.radius)

            this.tooltip.active = false
            this.collide.initialize(this.simulation.nodes())
            this.simulation.alpha(1).restart()
        },
        onclick(d) {
            this.$emit('tissue-click', d.key)
        },
    },
    mounted() {
        this.link = this.svg.append('g')
            .attr('class', 'links')
            .selectAll('line')
            .data(this.links)
            .enter().append('line')
            .attr('stroke-width', 3)
            .attr('stroke-color', '#fff')

        this.node = this.svg.append('g')
            .attr('class', 'nodes')
            .selectAll('circle')
            .data(this.tissues)
            .enter()
            .append('circle')
            .attr('id', d => d.key)
            .attr('r', () => this.radius)
            .attr('fill', '#000')
            .on('mouseover', this.mouseover)
            .on('mouseout', this.mouseout)
            .on('click', this.onclick)
            .each((d, i, nodes) => {
                let pattern = this.svg
                    .append('defs')
                    .append('pattern')
                let rect = pattern.append('rect')
                rect
                    .attr('fill', '#000')
                    .attr('width', '100%')
                    .attr('height', '100%')
                pattern
                    .attr('id', 'node-img-' + d.key)
                    .attr('patternContentUnits', 'objectBoundingBox')
                    .attr('width', '100%')
                    .attr('height', '100%')
                    .append('image')
                    .attr('height', 1)
                    .attr('width', 1)
                    .attr('preserveAspectRatio', 'none')
                    .attr('xlink:href', d.images.thumb)

                select(nodes[i]).attr('fill', 'url(#node-img-' + d.key + ')')
            })

        this.simulation
            .nodes(this.tissues)
            .on('tick', this.ticked)

        this.simulation.force('link')
            .links(this.links)

    },
    components: {
        Tooltip,
    },
}
</script>
